"use client";
import React, { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import Link from "next/link";

// Import SVG icons as static images
import IconMockTest from "@/assets/images/common/icon-mock-test.svg";
import IconStudy from "@/assets/images/common/icon-study.svg";
import IconMissions from "@/assets/images/common/icon-missions.svg";
import IconMistakes from "@/assets/images/common/icon-mistakes.svg";
import IconVocabulary from "@/assets/images/common/icon-vocabulary.svg";
import IconProfile from "@/assets/images/common/icon-profile.svg";
import IconHistory from "@/assets/images/common/icon-history.svg";
import IconNotifications from "@/assets/images/common/icon-notifications.svg";
import IconBtnExpand from "@/assets/images/common/btn-expand.svg";
import IconSwitchTeacher from "@/assets/images/common/icon-switch-teacher.svg";
import { isMobile } from "@flyer/utils";
import { twMerge } from "tailwind-merge";
import { NotificationPopupLazy, SignInModalLazy } from "@/components/popups";
import { useModal } from "@flyer/components";
import { useAuth } from "@/providers/auth";
import useSignUpFlowModal from "@/components/popups/auth/useSignUpFlowModal";
import useForgotPasswordModal from "@/components/popups/auth/useForgotPasswordModal";
import { ENVIRONMENT } from "@/lib/config";
import ROUTES, { ORIGINAL_DOMAINS } from "@/lib/routes";
import { TestLevelBySlugQuery } from "@/api/common";
import { useQuery } from "@apollo/client";

export interface SidebarMenuItem {
  key: string;
  title: string;
  icon: JSX.Element;
  path?: string;
  onClick?: () => void;
  active?: boolean;
}

interface SidebarMenuProps {
  activeKey: string;
  onItemClick?: (item: SidebarMenuItem) => void;
  isCollapsed?: boolean;
  toggleSidebar?: () => void;
}

function SidebarMenu({
  activeKey: propActiveKey,
  onItemClick,
  isCollapsed = false,
  toggleSidebar,
}: SidebarMenuProps) {
  const { t: ieltsTranslation } = useTranslation("ielts");
  const router = useRouter();
  const { isLogin } = useAuth();
  const { openSignUp } = useSignUpFlowModal();
  const { openForgotPassword } = useForgotPasswordModal();
  const { toggleModal: toggleSignIn, hideModal: hideSignIn } =
    useModal(SignInModalLazy);

  const handleOpenSignIn = () => {
    toggleSignIn({
      onSignUpClick: () => {
        hideSignIn();
        openSignUp(() => {
          toggleSignIn({
            onSignUpClick: () => {
              hideSignIn();
              openSignUp(() => handleOpenSignIn());
            },
            onForgotPasswordClick: () => {
              hideSignIn();
              openForgotPassword({ onSignInClick: () => handleOpenSignIn() });
            },
          });
        });
      },
      onForgotPasswordClick: () => {
        hideSignIn();
        openForgotPassword({ onSignInClick: () => handleOpenSignIn() });
      },
    });
  };

  const determineActiveKey = () => {
    const path = router.pathname;
    const query = router.query;

    // Study and practice lists were merged into /libraries; the practice detail
    // routes still live under /practice/{id} and belong to the same menu item.
    if (path.startsWith("/libraries") || path.match(/\/practice\/[^/]+$/)) {
      return "libraries";
    }

    if (path === "/") return "mock-test";
    if (path.includes("/mission")) return "mission";
    if (path.includes("/mistakes") && query.currentTabMistakes === "notes")
      return "vocabulary";
    if (path.includes("/mistakes")) return "mistakes";
    if (path.includes("/vocabulary")) return "vocabulary";
    if (path.includes("/learning-profile")) return "learningProfile";
    if (path.includes("/history")) return "history";

    return propActiveKey || "";
  };

  const initialActiveKey = determineActiveKey();
  const [activeKey, setActiveKey] = useState<string>(initialActiveKey);

  useEffect(() => {
    const newActiveKey = determineActiveKey();
    setActiveKey(newActiveKey);
  }, [router.pathname, router.query]);

  const getThemedIcon = (Icon: any, props?: any) => {
    return <Icon {...props} />;
  };

  const menuItems: SidebarMenuItem[] = [
    {
      key: "mock-test",
      title: ieltsTranslation("homepage.mockTest"),
      icon: getThemedIcon(IconMockTest),
      path: "/",
      active: activeKey === "mock-test",
    },
    {
      key: "libraries",
      title: ieltsTranslation("homepage.libraries"),
      icon: getThemedIcon(IconStudy),
      path: ROUTES.LIBRARIES,
      active: activeKey === "libraries",
    },
    {
      key: "mission",
      title: ieltsTranslation("homepage.missions"),
      icon: getThemedIcon(IconMissions),
      path: "/mission",
      active: activeKey === "mission",
    },
    {
      key: "mistakes",
      title: ieltsTranslation("homepage.menuMistakes"),
      icon: getThemedIcon(IconMistakes),
      path: "/mistakes",
      active: activeKey === "mistakes",
    },
    {
      key: "vocabulary",
      title: ieltsTranslation("homepage.vocabularyBook"),
      icon: getThemedIcon(IconVocabulary),
      path: "/mistakes?currentTabMistakes=notes",
      active: activeKey === "vocabulary",
    },
    {
      key: "learningProfile",
      title: ieltsTranslation("homepage.learningProfile"),
      icon: getThemedIcon(IconProfile),
      path: "/learning-profile", //'/edit-profile',
      active: activeKey === "learningProfile",
    },
    {
      key: "history",
      title: ieltsTranslation("homepage.history"),
      icon: getThemedIcon(IconHistory),
      path: "/history",
      active: activeKey === "history",
    },
  ];

  const handleItemClick = (item: SidebarMenuItem) => {
    if (onItemClick) {
      onItemClick(item);
    } else if (item.onClick) {
      item.onClick();
    }
    // Items with a `path` are navigated by the <Link> itself
  };

  return (
    <nav className="flex-1 min-h-0 overflow-y-auto relative w-full">
      <ul className="space-y-1">
        {isCollapsed && (
          <li>
            <Link
              href={"#"}
              className={twMerge(
                "flex items-center gap-3 p-1 transition-all duration-500 ease-in-out hover:bg-ielts-tint hover:rounded-full",
                isCollapsed ? "justify-center" : "",
                "text-ielts-fg-secondary hover:text-ielts-accent",
              )}
              onClick={(e) => {
                e.preventDefault();
                if (toggleSidebar) toggleSidebar();
              }}
            >
              <span className="w-[43px] h-[39px] flex items-center justify-center">
                {getThemedIcon(IconBtnExpand)}
              </span>
            </Link>
          </li>
        )}
        {menuItems.map((item) => (
          <li key={item.key}>
            <Link
              href={item.path || "#"}
              className={twMerge(
                // `group` so the icon can pick up the accent on hover; the row
                // always reserves its border so the active state doesn't shift
                // the label by a pixel.
                "group flex items-center gap-3 p-3 transition-all duration-300 ease-in-out text-[16px] rounded-[10px] border-[1px] border-transparent",
                isCollapsed ? "justify-center" : "",
                // The two isMobile() overrides that used to force #515159 here
                // existed only because the mobile sidebar panel was hardcoded
                // bg-white; it now reads `surface`, so one token covers both.
                item.active
                  ? "border-ielts-tint-line bg-ielts-tint text-ielts-accent font-[600]"
                  : "text-ielts-fg-secondary hover:border-ielts-tint-line hover:bg-ielts-tint hover:text-ielts-accent",
              )}
              onClick={(e) => {
                if (!item.path) {
                  e.preventDefault();
                }

                if (
                  isLogin ||
                  item.key === "mock-test" ||
                  item.key === "libraries"
                ) {
                  handleItemClick(item);
                } else {
                  e.preventDefault();
                  handleOpenSignIn();
                }
              }}
            >
              <span
                className={twMerge(
                  "w-5 h-5 flex items-center justify-center shrink-0",
                  // Every sidebar icon draws in currentColor, so the accent
                  // reaches them through this span alone.
                  item.active
                    ? "text-ielts-accent"
                    : "text-ielts-fg-muted group-hover:text-ielts-accent",
                )}
              >
                {item.icon}
              </span>
              <span
                className={twMerge(
                  "overflow-hidden transition-all duration-500 ease-in-out whitespace-nowrap",
                  isCollapsed ? "hidden opacity-0" : "block w-auto opacity-100",
                )}
              >
                {item.title}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default SidebarMenu;

export function NotificationsMenuItem({
  isCollapsed = false,
}: {
  isCollapsed?: boolean;
}) {
  const { t } = useTranslation("ielts");
  const { toggleModal: toggleNotification } = useModal(NotificationPopupLazy);

  // The icon draws in currentColor; the wrapper span owns the colour so it can
  // follow the row into the accent on hover.
  const getThemedIcon = (Icon: any, props?: any) => {
    return <Icon {...props} />;
  };

  return (
    <Link
      href="#"
      className={twMerge(
        "group flex items-center gap-3 md:p-1 lg:p-3 transition-all duration-300 ease-in-out rounded-[10px] hover:bg-ielts-tint p-[22px] py-[12px]",
        isCollapsed ? "justify-center" : "",
        "text-ielts-fg-secondary hover:text-ielts-accent",
      )}
      onClick={(e) => {
        e.preventDefault();
        toggleNotification();
      }}
    >
      <span
        className={twMerge(
          "w-5 h-5 flex items-center justify-center shrink-0",
          "text-ielts-fg-muted group-hover:text-ielts-accent",
        )}
      >
        {getThemedIcon(IconNotifications, {
          alt: "Notifications",
          width: 20,
          height: 20,
        })}
      </span>
      {!isMobile() && (
        <span
          className={twMerge(
            "overflow-hidden transition-all duration-500 ease-in-out whitespace-nowrap text-[16px] font-[500]",
            isCollapsed ? "hidden opacity-0" : "block opacity-100",
          )}
        >
          {t("homepage.notifications")}
        </span>
      )}
    </Link>
  );
}

export function SwtchTeacherMenuItem({
  isCollapsed = false,
}: {
  isCollapsed?: boolean;
}) {
  const { t, lang } = useTranslation("ielts");
  const { data: dataLevel } = useQuery(TestLevelBySlugQuery, {
    variables: {
      slug: "ielts-speaking",
    },
    context: {
      v2: true,
    },
  });

  const handleClickTeacher = useCallback(() => {
    const basePath = "/explore";
    const ieltsSlug = dataLevel?.testLevelBySlug?.slug;
    const parts = window.location.hostname.split(".");
    const subDomain = parts[0];
    const queryParams = new URLSearchParams();
    if (
      subDomain &&
      !ORIGINAL_DOMAINS.includes(subDomain) &&
      subDomain !== "localhost"
    ) {
      queryParams.set("custom_domain", subDomain);
    }
    if (ieltsSlug) {
      queryParams.set("testLevel", ieltsSlug);
    }
    const queryString = queryParams.toString();
    const url = `${ENVIRONMENT.PARTNER_URL}/${lang}${basePath}${queryString ? `?${queryString}` : ""}`;
    window.open(url, "_blank");
  }, [lang, dataLevel?.testLevelBySlug?.slug]);

  const getThemedIcon = (Icon: any, props?: any) => {
    return <Icon {...props} />;
  };

  return (
    <Link
      href="#"
      className={twMerge(
        "group flex items-center gap-3 md:p-1 lg:p-3 transition-all duration-300 ease-in-out rounded-[10px] hover:bg-ielts-tint p-[22px] py-[12px]",
        isCollapsed ? "justify-center" : "",
        "text-ielts-fg-secondary hover:text-ielts-accent",
      )}
      onClick={(e) => {
        e.preventDefault();
        handleClickTeacher();
      }}
    >
      <span
        className={twMerge(
          "w-5 h-5 flex items-center justify-center shrink-0",
          "text-ielts-fg-muted group-hover:text-ielts-accent",
        )}
      >
        {getThemedIcon(IconSwitchTeacher, {
          alt: "IconSwitchTeacher",
          width: 20,
          height: 20,
        })}
      </span>
      {!isMobile() && (
        <span
          className={twMerge(
            "overflow-hidden transition-all duration-500 ease-in-out whitespace-nowrap text-[16px] font-[500]",
            isCollapsed ? "hidden opacity-0" : "block opacity-100",
          )}
        >
          {t("homepage.switch_teacher_role")}
        </span>
      )}
    </Link>
  );
}
